import { FunctionComponent } from 'react';
import { NextPage } from 'next';
import Link from 'next/link';
import { gql } from '@apollo/client';
import styled, { css } from 'styled-components';
import { Header, HEADER_QUERY } from '../components/header';
import { Footer, FOOTER_QUERY } from '../components/footer';
import { ContentStackClient } from '../lib/contentstack';
import { mediaScreen } from '../utils/mediaScreen';
import { IPageProps } from '../types/page';
import { Manifesto2025 } from '../types/contentstack';
import { SENSOR_FUSION_PATH } from '../content/sensor-fusion';

// Shared structural shape for the per-section `paragraphs` groups.
// All four sections generate identical fields under distinct nominal types.
type Paragraph = {
  variant?: string | null;
  label?: string | null;
  text?: string | null;
  highlight?: string | null;
};

/* ------------------------------------------------------------------ *
 *  Layout primitives
 * ------------------------------------------------------------------ */

// Wide wrapper for hero + CTA bands (artifact: max-width 1180).
const WideWrap = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;

  ${mediaScreen('xs', 'md')} {
    padding: 0 22px;
  }
`;

// Narrow reading column for prose sections (artifact: max-width 760).
const ProseWrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;

  ${mediaScreen('xs', 'md')} {
    padding: 0 22px;
  }
`;

/* ------------------------------------------------------------------ *
 *  Hero
 * ------------------------------------------------------------------ */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.slate1};

  .hero-dots {
    position: absolute;
    inset: 0;
    background-image: url('/img/dots-bg.svg');
    background-size: cover;
    background-position: center;
    opacity: 0.35;
  }

  .hero-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      1000px 560px at 76% 6%,
      rgba(81, 192, 172, 0.16),
      transparent 62%
    );
  }

  .hero-fade {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 200px;
    background: linear-gradient(
      180deg,
      transparent,
      ${({ theme }) => theme.colors.slate1}
    );
  }

  .hero-inner {
    padding: 168px 0 104px;

    ${mediaScreen('xs', 'md')} {
      padding: 128px 0 72px;
    }
  }
`;

const HeroEyebrow = styled.div`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.teal};
  margin-bottom: 30px;
`;

const HeroHeadline = styled.h1`
  font-size: clamp(40px, 5.6vw, 76px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -1.4px;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
  max-width: 980px;

  span {
    color: ${({ theme }) => theme.colors.teal};
    display: block;
  }
`;

const HeroIntro = styled.div`
  max-width: 720px;
  margin-top: 48px;
  font-size: 21px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.78);
  font-weight: 400;

  p {
    margin: 0 0 24px;
  }
  p:last-child {
    margin: 0;
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 32px;
    font-size: 18px;
  }
`;

/* ------------------------------------------------------------------ *
 *  Manifesto sections (dark / light)
 * ------------------------------------------------------------------ */

const Section = styled.section<{ light?: boolean; flush?: boolean }>`
  padding: 76px 0;
  background: ${({ theme, light }) =>
    light ? theme.colors.white : theme.colors.slate1};

  ${({ flush }) =>
    flush &&
    css`
      padding-top: 0;
    `}

  ${mediaScreen('xs', 'md')} {
    padding: 52px 0;
    ${({ flush }) =>
      flush &&
      css`
        padding-top: 0;
      `}
  }
`;

const SectionRule = styled.div<{ light?: boolean }>`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 30px;

  .num {
    font-family: 'SFMono-Regular', ui-monospace, 'Menlo', monospace;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 1px;
    color: ${({ theme, light }) =>
      light ? theme.colors.eb1 : theme.colors.teal};
  }

  .line {
    flex: 1;
    height: 1px;
    background: ${({ light }) =>
      light ? 'rgba(25,31,44,0.12)' : 'rgba(128,146,178,0.22)'};
  }
`;

const SectionHeadline = styled.h2<{ light?: boolean }>`
  font-size: clamp(30px, 3.6vw, 40px);
  font-weight: 700;
  line-height: 1.14;
  letter-spacing: -0.6px;
  color: ${({ theme, light }) =>
    light ? theme.colors.slate1 : theme.colors.white};
  margin: 0 0 34px;
  max-width: 620px;
`;

const Prose = styled.div<{ light?: boolean }>`
  font-size: 20px;
  line-height: 1.72;
  font-weight: 400;
  color: ${({ light }) =>
    light ? 'rgba(25,31,44,0.80)' : 'rgba(255,255,255,0.80)'};

  ${mediaScreen('xs', 'md')} {
    font-size: 18px;
  }

  p {
    margin: 0 0 24px;
  }

  /* labeled lead-in word (Perceive. / Reason. / Act.) */
  .lead-in {
    font-weight: 700;
    color: ${({ theme, light }) =>
      light ? theme.colors.eb1 : theme.colors.teal};
  }

  /* inline highlight fragment appended to a body paragraph */
  .inline-hl {
    color: ${({ theme, light }) =>
      light ? theme.colors.slate1 : theme.colors.white};
    font-weight: 500;
  }

  /* large emphasized line */
  .emphasis {
    font-size: 27px;
    line-height: 1.3;
    font-weight: 500;
    color: ${({ theme, light }) =>
      light ? theme.colors.slate1 : theme.colors.white};
    margin: 0 0 24px;

    ${mediaScreen('xs', 'md')} {
      font-size: 23px;
    }
  }

  /* inline-weight statement line */
  .statement {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.slate1};
    margin: 0 0 32px;
  }

  blockquote {
    margin: 40px 0;
    padding: 6px 0 6px 30px;
    border-left: 2px solid ${({ theme }) => theme.colors.teal};

    p {
      margin: 0;
      font-size: 30px;
      line-height: 1.34;
      font-weight: 300;
      letter-spacing: -0.4px;
      color: ${({ theme, light }) =>
        light ? theme.colors.slate1 : theme.colors.white};

      ${mediaScreen('xs', 'md')} {
        font-size: 24px;
      }
    }

    .quote-hl {
      color: ${({ theme }) => theme.colors.teal};
    }
  }
`;

/* ------------------------------------------------------------------ *
 *  Callout + stat band (section 03)
 * ------------------------------------------------------------------ */

const Callout = styled.div`
  display: flex;
  gap: 18px;
  padding: 24px 26px;
  margin: 0 0 40px;
  background: rgba(25, 31, 44, 0.04);
  border: 1px solid rgba(25, 31, 44, 0.1);
  border-left: 3px solid ${({ theme }) => theme.colors.teal};
  border-radius: 10px;

  p {
    margin: 0;
    font-size: 18px;
    line-height: 1.6;
    font-weight: 400;
    color: rgba(25, 31, 44, 0.82);
  }
  strong {
    color: ${({ theme }) => theme.colors.slate1};
    font-weight: 700;
  }
`;

const StatsEyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.slate5};
  margin: 0 0 20px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid rgba(25, 31, 44, 0.12);
  border-radius: 14px;
  overflow: hidden;
  background: rgba(25, 31, 44, 0.1);
  gap: 1px;

  ${mediaScreen('xs', 'md')} {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat {
    background: ${({ theme }) => theme.colors.white};
    padding: 28px 22px;
  }
  .stat .value {
    font-size: 38px;
    font-weight: 700;
    letter-spacing: -1px;
    color: ${({ theme }) => theme.colors.eb1};
    line-height: 1;
  }
  .stat .label {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.slate4};
    margin-top: 10px;
    line-height: 1.4;
  }
`;

const ClosingLine = styled.p`
  font-size: 27px;
  line-height: 1.3;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.slate1};
  margin: 38px 0 0;

  span {
    color: ${({ theme }) => theme.colors.eb1};
  }

  ${mediaScreen('xs', 'md')} {
    font-size: 23px;
  }
`;

// A single line under section 03 that points at the Sensor Fusion page.
const FusionLink = styled.p`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin: 34px 0 0;
  padding-top: 26px;
  border-top: 1px solid rgba(25, 31, 44, 0.12);
  font-size: 19px;
  line-height: 1.5;
  color: rgba(25, 31, 44, 0.82);

  .tag {
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.eb1};
    border-radius: 99px;
    padding: 3px 11px;
  }

  a {
    color: ${({ theme }) => theme.colors.eb1};
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.eb1};
    outline-offset: 3px;
    border-radius: 2px;
  }

  ${mediaScreen('xs', 'md')} {
    font-size: 17px;
  }
`;

/* ------------------------------------------------------------------ *
 *  Paragraph renderer
 * ------------------------------------------------------------------ */

const Paragraphs: FunctionComponent<{
  items?: (Paragraph | null)[] | null;
  light?: boolean;
}> = ({ items }) => (
  <>
    {(items ?? []).map((p, i) => {
      if (!p) return null;
      const key = `${p.variant}-${i}`;

      if (p.variant === 'emphasis') {
        return (
          <p key={key} className="emphasis">
            {p.text}
          </p>
        );
      }

      if (p.variant === 'statement') {
        return (
          <p key={key} className="statement">
            {p.text}
          </p>
        );
      }

      if (p.variant === 'quote') {
        return (
          <blockquote key={key}>
            <p>
              {p.text}
              {p.highlight && (
                <>
                  {' '}
                  <span className="quote-hl">{p.highlight}</span>
                </>
              )}
              {p.label}
            </p>
          </blockquote>
        );
      }

      if (p.variant === 'labeled') {
        return (
          <p key={key}>
            <span className="lead-in">{p.label}</span> {p.text}
          </p>
        );
      }

      // body (default)
      return (
        <p key={key}>
          {p.text}
          {p.highlight && (
            <>
              {' '}
              <span className="inline-hl">{p.highlight}</span>
            </>
          )}
        </p>
      );
    })}
  </>
);

/* ------------------------------------------------------------------ *
 *  Page
 * ------------------------------------------------------------------ */

const ManifestoPage: NextPage<IPageProps<Manifesto2025>> = ({
  header,
  page,
  footer,
}) => {
  const hero = page?.hero;
  const problem = page?.problem;
  const needs = page?.needs;
  const mapped = page?.mapped;
  const foundation = page?.foundation;
  const cta = page?.closing_cta;

  return (
    <>
      <Header
        pageTitle={page?.seo?.meta_title ?? page?.title}
        pageDescription={page?.seo?.meta_description}
        {...header}
      />

      {/* HERO */}
      <Hero>
        <div className="hero-dots" />
        <div className="hero-glow" />
        <div className="hero-fade" />
        <WideWrap>
          <div className="hero-inner">
            {hero?.eyebrow && <HeroEyebrow>{hero.eyebrow}</HeroEyebrow>}
            <HeroHeadline>
              {hero?.headline}{' '}
              {hero?.headline_highlight && (
                <span>{hero.headline_highlight}</span>
              )}
            </HeroHeadline>
            <HeroIntro>
              {(hero?.intro ?? []).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </HeroIntro>
          </div>
        </WideWrap>
      </Hero>

      {/* 01 — dark */}
      <Section>
        <ProseWrap>
          <SectionRule>
            <span className="num">{problem?.number}</span>
            <span className="line" />
          </SectionRule>
          <SectionHeadline>{problem?.headline}</SectionHeadline>
          <Prose>
            <Paragraphs items={problem?.paragraphs} />
          </Prose>
        </ProseWrap>
      </Section>

      {/* 02 — light */}
      <Section light>
        <ProseWrap>
          <SectionRule light>
            <span className="num">{needs?.number}</span>
            <span className="line" />
          </SectionRule>
          <SectionHeadline light>{needs?.headline}</SectionHeadline>
          <Prose light>
            <Paragraphs items={needs?.paragraphs} light />
          </Prose>
        </ProseWrap>
      </Section>

      {/* 03 — light (continues white block) */}
      <Section light flush>
        <ProseWrap>
          <SectionRule light>
            <span className="num">{mapped?.number}</span>
            <span className="line" />
          </SectionRule>
          <SectionHeadline light>{mapped?.headline}</SectionHeadline>
          <Prose light>
            <Paragraphs items={mapped?.paragraphs} light />
          </Prose>

          {mapped?.callout && (
            <Callout>
              <p>
                <strong>{mapped.callout.label}</strong> {mapped.callout.text}
              </p>
            </Callout>
          )}

          {mapped?.stats_eyebrow && (
            <StatsEyebrow>{mapped.stats_eyebrow}</StatsEyebrow>
          )}
          {!!mapped?.stats?.length && (
            <StatGrid>
              {mapped.stats.map((s, i) => (
                <div className="stat" key={i}>
                  <div className="value">{s?.value}</div>
                  <div className="label">{s?.label}</div>
                </div>
              ))}
            </StatGrid>
          )}

          {mapped?.closing_line && (
            <ClosingLine>
              {mapped.closing_line}{' '}
              {mapped.closing_highlight && (
                <span>{mapped.closing_highlight}</span>
              )}
            </ClosingLine>
          )}

          {/* Native link to the Sensor Fusion page; not Contentstack content. */}
          <FusionLink>
            <span className="tag">New</span>
            <span className="text">
              Sensor Fusion adds where everything is, live.{' '}
              <Link href={SENSOR_FUSION_PATH}>See how it works</Link>
            </span>
          </FusionLink>
        </ProseWrap>
      </Section>

      {/* 04 — dark */}
      <Section>
        <ProseWrap>
          <SectionRule>
            <span className="num">{foundation?.number}</span>
            <span className="line" />
          </SectionRule>
          <SectionHeadline>{foundation?.headline}</SectionHeadline>
          <Prose>
            <Paragraphs items={foundation?.paragraphs} />
          </Prose>
        </ProseWrap>
      </Section>

      {/* CLOSING CTA — reuses the standard Footer pre-footer CTA band */}
      <Footer
        eyebrow={cta?.eyebrow}
        headline={cta?.headline}
        primary_button={cta?.primary_cta}
        secondary_button={cta?.secondary_cta}
        sections={footer?.sections}
      />
    </>
  );
};

export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<{
      page: { data: Manifesto2025[] };
    }>({
      query: PAGE_QUERY,
    });

    const page = data?.page?.data?.[0];

    return { props: { ...data, page } };
  } catch (e) {
    return { props: {} };
  }
}

export default ManifestoPage;

const PAGE_QUERY = gql`
  query manifestoQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: all_manifesto_2025 {
      data: items {
        title
        url
        hero {
          eyebrow
          headline
          headline_highlight
          intro
        }
        problem {
          number
          headline
          paragraphs {
            variant
            label
            text
            highlight
          }
        }
        needs {
          number
          headline
          paragraphs {
            variant
            label
            text
            highlight
          }
        }
        mapped {
          number
          headline
          paragraphs {
            variant
            label
            text
            highlight
          }
          callout {
            label
            text
          }
          stats_eyebrow
          stats {
            value
            label
          }
          closing_line
          closing_highlight
        }
        foundation {
          number
          headline
          paragraphs {
            variant
            label
            text
            highlight
          }
        }
        closing_cta {
          eyebrow
          headline
          primary_cta {
            title
            href
          }
          secondary_cta {
            title
            href
          }
        }
        seo {
          meta_title
          meta_description
        }
      }
    }
  }
`;
